import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Copy,
  CheckCircle2,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  Building2,
  Zap,
} from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Settings() {
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  if (!s) return <div className="p-10 text-muted-foreground">Loading…</div>;

  const channels = s.channels || [];
  const metaCh = channels.find((c) => c.type === "meta");
  const interaktCh = channels.find((c) => c.type === "interakt");

  return (
    <div className="px-10 py-10">
      <header className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.24em] text-muted-foreground">Integration</div>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl" data-testid="settings-title">
          Channels &amp; Webhooks
        </h1>
        <p className="mt-3 max-w-xl text-sm text-muted-foreground">
          Configure your BSPs, share callback URLs with Meta / Interakt, and inspect live webhook traffic to diagnose
          any connection issues.
        </p>
      </header>

      <Tabs defaultValue="channels">
        <TabsList className="rounded-none border-b border-border bg-transparent p-0">
          <TabsTrigger value="channels" className="rounded-none border-b-2 border-transparent px-4 py-2 data-[state=active]:border-primary data-[state=active]:bg-transparent" data-testid="tab-channels">
            Channels
          </TabsTrigger>
          <TabsTrigger value="activity" className="rounded-none border-b-2 border-transparent px-4 py-2 data-[state=active]:border-primary data-[state=active]:bg-transparent" data-testid="tab-activity">
            Webhook Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="channels" className="mt-6 space-y-8">
          {metaCh && (
            <ChannelCard
              icon={Building2}
              label="Direct Meta WABA"
              badgeClass="border-primary/40 bg-primary/10 text-primary"
              callbackUrl={`${backendUrl}${metaCh.webhook_path}`}
              rows={[
                ["Phone", metaCh.phone || "—"],
                ["WABA ID", metaCh.waba_id],
                ["Phone Number ID", metaCh.phone_number_id],
                ["Graph version", metaCh.graph_version],
                ["Access token", metaCh.access_token_masked],
                ["App secret", metaCh.app_secret_masked],
                ["Verify token", metaCh.verify_token, "mono"],
              ]}
              copy={copy}
              instructions={[
                "Meta App Dashboard → WhatsApp → Configuration.",
                "Paste the callback URL and verify token below.",
                "Subscribe to messages and message_status.",
              ]}
            />
          )}
          {interaktCh && (
            <ChannelCard
              icon={Zap}
              label="Interakt BSP"
              badgeClass="border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300"
              callbackUrl={`${backendUrl}${interaktCh.webhook_path}`}
              rows={[
                ["Phone", interaktCh.phone || "—"],
                ["API key", interaktCh.api_key_masked],
                ["Webhook secret", interaktCh.webhook_secret_masked],
                ["Active", interaktCh.active ? "Yes" : "No"],
              ]}
              copy={copy}
              instructions={[
                "Interakt Dashboard → Developer Settings → Webhooks.",
                "Paste the callback URL below. The webhook secret is already stored on our side.",
                "Subscribe to message_received and message_status.",
              ]}
            />
          )}
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          <WebhookActivity />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ChannelCard({ icon: Icon, label, badgeClass, callbackUrl, rows, copy, instructions }) {
  return (
    <section className="border border-border">
      <header className="flex items-center justify-between border-b border-border bg-secondary/40 px-6 py-4">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 border px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest ${badgeClass}`}>
            <Icon className="h-3 w-3" aria-hidden="true" />
            {label.toUpperCase()}
          </span>
          <h2 className="font-display text-lg font-bold tracking-tight">{label}</h2>
        </div>
      </header>
      <div className="grid grid-cols-1 gap-0 md:grid-cols-2">
        {rows.map(([k, v, kind]) => (
          <div
            key={k}
            className="border-border p-4 [&:not(:last-child)]:border-b md:[&:nth-child(odd)]:border-r md:[&:not(:nth-last-child(-n+2))]:border-b"
          >
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">{k}</div>
            <div className={`mt-1 truncate ${kind === "mono" ? "font-mono text-sm" : "font-display text-base font-bold"}`}>{v || "—"}</div>
          </div>
        ))}
      </div>
      <div className="border-t border-border p-6">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.24em] text-emerald-600 dark:text-emerald-400">
          <CheckCircle2 className="h-4 w-4" /> Callback URL
        </div>
        <div className="mt-3 flex flex-col items-start gap-3 md:flex-row md:items-center">
          <code className="w-full break-all border border-border bg-secondary px-3 py-2 font-mono text-sm">{callbackUrl}</code>
          <Button variant="outline" onClick={() => copy(callbackUrl, "Callback URL")} className="shrink-0 rounded-sm">
            <Copy className="mr-2 h-4 w-4" /> Copy
          </Button>
        </div>
        <ol className="mt-6 list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
          {instructions.map((it, i) => <li key={i}>{it}</li>)}
        </ol>
      </div>
    </section>
  );
}

function WebhookActivity() {
  const [source, setSource] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState(null);

  const load = () => {
    setLoading(true);
    const q = source === "all" ? "" : `?source=${source}`;
    api.get(`/webhook-logs${q}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(load, [source]);

  const c = data?.counts || {};
  const entries = data?.entries || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-1 border border-border">
          {["all", "meta", "interakt"].map((s) => (
            <button
              key={s}
              onClick={() => setSource(s)}
              data-testid={`activity-filter-${s}`}
              className={`px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors ${
                source === s ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
        <Button variant="outline" onClick={load} className="rounded-sm" data-testid="refresh-activity-btn">
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-0 border border-border md:grid-cols-5">
        <Kpi label="Delivered" value={c.signed_ok ?? 0} good />
        <Kpi label="Unsigned OK" value={c.unsigned_accepted ?? 0} warn />
        <Kpi label="Bad signature" value={c.bad_signature ?? 0} bad />
        <Kpi label="Invalid JSON" value={c.invalid_json ?? 0} bad />
        <Kpi label="Retention" value={`${data?.retention_hours ?? 48}h`} />
      </div>

      <div className="border border-border">
        <div className="border-b border-border bg-secondary px-4 py-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
          Last {entries.length} attempts
        </div>
        <ScrollArea className="max-h-[520px]">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="p-10 text-center">
              <ShieldAlert className="mx-auto h-8 w-8 text-muted-foreground" />
              <div className="mt-3 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">No webhook traffic yet</div>
              <p className="mx-auto mt-2 max-w-md text-xs text-muted-foreground">
                Nothing has hit this endpoint in the last 48 hours. If Interakt is configured but nothing appears here,
                confirm the callback URL in the Interakt dashboard exactly matches the one on the Channels tab.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {entries.map((e, i) => {
                const isOpen = openIdx === i;
                return (
                  <li key={e.id || i} data-testid={`activity-entry-${i}`}>
                    <button
                      onClick={() => setOpenIdx(isOpen ? null : i)}
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-secondary"
                    >
                      {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                      <VerdictBadge verdict={e.verdict} />
                      <span className="inline-flex items-center gap-1.5 border border-border px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest">
                        {e.source === "interakt" ? <Zap className="h-2.5 w-2.5" /> : <Building2 className="h-2.5 w-2.5" />}
                        {e.source}
                      </span>
                      <span className="text-xs text-muted-foreground">HTTP {e.status_code}</span>
                      {e.event_type && (
                        <span className="rounded-sm border border-border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                          {e.event_type}
                        </span>
                      )}
                      <span className="ml-auto font-mono text-[10px] text-muted-foreground">{fmtDate(e.received_at)}</span>
                    </button>
                    {isOpen && (
                      <div className="border-t border-border bg-secondary/30 px-4 py-4 text-xs">
                        <Detail label="Client IP">{e.client_ip || "—"}</Detail>
                        <Detail label="Path">{e.path}</Detail>
                        {e.provided_signature !== undefined && <Detail label="Provided signature" mono>{e.provided_signature || "(none)"}</Detail>}
                        {e.expected_prefix && <Detail label="Expected prefix" mono>{e.expected_prefix}…</Detail>}
                        <Detail label="Body preview" mono block>{e.body_preview || "(empty)"}</Detail>
                        <Detail label="Request headers" mono block>
                          {Object.entries(e.headers || {}).map(([k, v]) => `${k}: ${v}`).join("\n")}
                        </Detail>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}

function Kpi({ label, value, good, warn, bad }) {
  const cls = good ? "text-emerald-600 dark:text-emerald-400"
    : warn ? "text-amber-600 dark:text-amber-400"
    : bad ? "text-red-600 dark:text-red-400"
    : "text-foreground";
  return (
    <div className="border-border p-4 [&:not(:last-child)]:border-b md:[&:not(:last-child)]:border-b-0 md:[&:not(:last-child)]:border-r">
      <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-2xl font-black ${cls}`}>{value}</div>
    </div>
  );
}

function VerdictBadge({ verdict }) {
  const map = {
    signed_ok: { label: "SIGNED OK", Icon: ShieldCheck, cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
    unsigned_accepted: { label: "UNSIGNED", Icon: ShieldAlert, cls: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300" },
    bad_signature: { label: "BAD SIG", Icon: ShieldAlert, cls: "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300" },
    invalid_json: { label: "BAD JSON", Icon: ShieldAlert, cls: "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300" },
    not_configured: { label: "NO CONFIG", Icon: ShieldAlert, cls: "border-neutral-500/40 bg-neutral-500/10 text-muted-foreground" },
  };
  const m = map[verdict] || { label: verdict || "—", Icon: ShieldAlert, cls: "border-border" };
  return (
    <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-widest ${m.cls}`}>
      <m.Icon className="h-2.5 w-2.5" aria-hidden="true" />
      {m.label}
    </span>
  );
}

function Detail({ label, children, mono, block }) {
  return (
    <div className={`grid gap-1 py-1 ${block ? "" : "md:grid-cols-[160px_1fr] md:gap-3"}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className={`${mono ? "font-mono" : ""} ${block ? "whitespace-pre-wrap break-all" : "truncate"}`}>{children}</div>
    </div>
  );
}
